import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { ContactForm } from './ContactForm';

const Hero: React.FC = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 lg:pt-0">
      {/* Background Elements */}
      <div className="absolute inset-0 gradient-bg"></div>
      
      {/* Floating Elements - Hidden on mobile for better performance */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-20 left-10 w-16 h-16 lg:w-32 lg:h-32 bg-primary-200 rounded-full opacity-20 blur-xl hidden sm:block"
      ></motion.div>
      <motion.div
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-20 right-10 w-20 h-20 lg:w-40 lg:h-40 bg-primary-300 rounded-full opacity-20 blur-xl hidden sm:block"
      ></motion.div>
      <motion.div
        animate={{ y: [0, -15, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/2 left-1/4 w-12 h-12 lg:w-24 lg:h-24 bg-secondary-200 rounded-full opacity-20 blur-xl hidden sm:block"
      ></motion.div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto mt-4 sm:mt-6 lg:mt-8"
        >
          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-neutral-900 leading-relaxed mb-2 sm:mb-3 px-4 pb-4"
            style={{ lineHeight: '1.2' }}
          >
            AI-Powered Solutions for
            <span className="block text-gradient mt-1 mb-4 pb-2" style={{ lineHeight: '1.2' }}>Healthcare Supply Chains</span>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-lg sm:text-xl md:text-2xl text-neutral-600 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4"
          >
            Helping hospitals respond faster, source smarter, save costs and support patient care
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-12 sm:mb-16 px-4"
          >
                         <motion.button
               whileHover={{ scale: 1.05 }}
               whileTap={{ scale: 0.95 }}
               onClick={() => setIsContactFormOpen(true)}
               className="btn-primary flex items-center justify-center gap-2 group w-full sm:w-auto px-6 py-3 sm:py-4 text-base sm:text-lg"
             >
               Request a Demo
               <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
             </motion.button>
            
            <motion.a
              href="https://drive.google.com/file/d/1jFmoTHy-89kX-aNMO5rIDBpgfkKbK73N/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary flex items-center justify-center gap-2 group w-full sm:w-auto px-6 py-3 sm:py-4 text-base sm:text-lg"
            >
              <Play size={18} className="group-hover:scale-110 transition-transform" />
              Watch Demo
            </motion.a>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-neutral-400 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-neutral-400 rounded-full mt-2"
          ></motion.div>
                 </motion.div>
       </motion.div>

       {/* Contact Form Modal */}
       <ContactForm
         isOpen={isContactFormOpen}
         onClose={() => setIsContactFormOpen(false)}
         title="Request a Demo"
         buttonText="Submit Request"
       />
     </section>
   );
 };

export { Hero }; 